import React from 'react';
import { ContentRendererProps } from './types';
import BrandThemeWrapper from '@/components/brand/BrandThemeWrapper';
import { marked } from 'marked';
import { CalendarDays, Clock, User, Tag } from 'lucide-react';
import { ClipboardCopy } from '../previews/ClipboardCopy';

// Define interfaces for the content structure
interface ContentSection {
  heading?: string;
  content: string;
}

interface BlogPostMetadata {
  excerpt?: string;
  tags?: string[];
  author?: string;
  meta_description?: string;
  call_to_action?: string;
  reading_time?: string;
  published_date?: string;
}

/**
 * Renderer for blog post content type
 */
export const BlogPostRenderer: React.FC<ContentRendererProps> = ({ content }) => {
  console.log('BlogPostRenderer - Rendering blog post:', content.name);
  
  // Extract blog post specific data
  const { 
    title, 
    content_sections = [], 
    bonus_metadata = {},
    image_url,
    ideal_media_description
  } = content.content_json;
  
  // Extract metadata
  const { 
    excerpt, 
    tags = [], 
    author, 
    call_to_action,
    reading_time = '5 min read',
    published_date = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  } = bonus_metadata as BlogPostMetadata;
  
  // Function to render markdown content as HTML
  const renderContent = (text: string) => {
    if (!text) return '';
    return marked.parse(text);
  };

  // Function to get all body text for copying
  const getBodyText = () => {
    return content_sections
      .map((section: ContentSection) => {
        const heading = section.heading ? `${section.heading}\n\n` : '';
        return heading + (section.content || '');
      })
      .join('\n\n');
  };
  
  return (
    <BrandThemeWrapper brandId={content.brand_id} contentType="blog-post">
      <div className="w-full h-full overflow-auto bg-white dark:bg-gray-900">
        {/* Hero Section with Background Gradient */}
        <div 
          style={{
            background: `linear-gradient(to bottom right, var(--brand-primary, #3b82f6), var(--brand-secondary, #10b981))`,
            paddingTop: 'var(--spacing-lg, var(--brand-spacing-lg, 3rem))',
            paddingBottom: 'var(--spacing-lg, var(--brand-spacing-lg, 3rem))',
            paddingLeft: 'var(--spacing-md, var(--brand-spacing-md, 1rem))',
            paddingRight: 'var(--spacing-md, var(--brand-spacing-md, 1rem))'
          }}
          className="text-white"
        >
          <div className="max-w-4xl mx-auto">
            <div className="group relative mb-4">
              <h1
                style={{
                  fontFamily: 'var(--font-heading, var(--brand-font-heading, var(--brand-font-primary, inherit)))',
                  fontSize: 'var(--text-4xl, 2.25rem)',
                  fontWeight: 'var(--font-weight-bold, 700)'
                }}
              >
                {title}
              </h1>
              <div className="absolute top-0 right-0">
                <ClipboardCopy
                  text={title}
                  variant="ghost"
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                />
              </div>
            </div>
            
            {excerpt && (
              <div className="group relative mb-6">
                <p
                  style={{
                    fontSize: 'var(--text-xl, 1.25rem)',
                    fontFamily: 'var(--font-body, var(--brand-font-primary, inherit))'
                  }}
                >
                  {excerpt}
                </p>
                <div className="absolute top-0 right-0">
                  <ClipboardCopy
                    text={excerpt}
                    variant="ghost"
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </div>
              </div>
            )}
            
            <div className="flex flex-wrap items-center gap-4 text-sm opacity-90">
              {author && (
                <div className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  <span>{author}</span>
                </div>
              )}
              
              <div className="flex items-center gap-1">
                <CalendarDays className="h-4 w-4" />
                <span>{published_date}</span>
              </div>
              
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>{reading_time}</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Header Image - displayed prominently after hero section */}
        {image_url && (
          <div className="w-full">
            <img
              src={image_url}
              alt={title || 'Blog post header image'}
              className="w-full max-h-96 object-cover"
              title={ideal_media_description || undefined}
            />
          </div>
        )}
        
        <article 
          style={{
            fontFamily: 'var(--font-body, var(--brand-font-primary, inherit))'
          }} 
          className="max-w-4xl mx-auto p-6"
        >
          {/* Tags displayed at the top of content */}
          {tags && tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8 mt-2">
              <Tag className="h-5 w-5 text-gray-500 mr-1" />
              {tags.map((tag: string, index: number) => (
                <span 
                  key={index}
                  style={{
                    backgroundColor: 'color-mix(in srgb, var(--brand-primary, #3b82f6) 10%, white)',
                    color: 'var(--brand-primary, #3b82f6)',
                    borderRadius: 'var(--border-radius-md, var(--brand-radius-md, 0.25rem))'
                  }} 
                  className="px-3 py-1 text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
          
          {/* Render content sections - no longer need image handling here */}
          <div className="group relative">
            <div className="absolute top-0 right-0 z-10">
              <ClipboardCopy
                text={getBodyText()}
                variant="ghost"
                className="opacity-0 group-hover:opacity-100 transition-opacity"
              />
            </div>
            <div
              style={{
                color: 'var(--text-primary-light, #212121)'
              }}
              className="prose prose-lg max-w-none dark:prose-invert"
            >
              {content_sections.map((section: ContentSection, index: number) => (
              <div key={index} className="mb-8">
                {section.heading && (
                  <h2 
                    style={{
                      fontFamily: 'var(--font-heading, var(--brand-font-heading, var(--brand-font-primary, inherit)))',
                      color: 'var(--text-primary-light, #212121)'
                    }}
                    className="text-2xl font-bold mt-8 mb-4"
                  >
                    {section.heading}
                  </h2>
                )}
                <div 
                  dangerouslySetInnerHTML={{ 
                    __html: renderContent(section.content || '') as string
                  }} 
                />
              </div>
            ))}
            
            {call_to_action && (
              <div 
                style={{
                  backgroundColor: 'color-mix(in srgb, var(--brand-primary, #3b82f6) 10%, white)',
                  borderRadius: 'var(--border-radius-lg, var(--brand-radius-lg, 0.5rem))',
                  padding: 'var(--spacing-md, var(--brand-spacing-md, 1rem))'
                }}
                className="mt-12"
              >
                <p 
                  style={{
                    color: 'var(--brand-primary, #3b82f6)'
                  }}
                  className="font-medium"
                >
                  {call_to_action}
                </p>
              </div>
            )}
            </div>
          </div>
        </article>
      </div>
    </BrandThemeWrapper>
  );
};

export default BlogPostRenderer; 